/**
 * 企业端 — 运输看板（本企业聚合）
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchEnterpriseDashboard } from '@/api/transit';
import { Empty, Loading, PageHeader, StatCard } from '@/components/common';
import type { EnterpriseDashboardData } from '@/types';
import { fmtTime } from '@/utils/format';

export default function EntDashboard() {
  const [data, setData] = useState<EnterpriseDashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEnterpriseDashboard().then((d) => {
      setData(d);
      setLoading(false);
    });
  }, []);

  if (loading) return <Loading />;
  if (!data) return <Empty />;

  return (
    <div>
      <PageHeader
        title="企业运输看板"
        sub="本企业运输单、在途车辆与告警总览"
        extra={
          <Link className="btn btn-primary" to="/enterprise/orders/new">
            ＋ 新建运输单
          </Link>
        }
      />

      <div className="grid grid-4" style={{ marginBottom: 16 }}>
        <StatCard label="在途运输单" value={data.in_transit_count} tone="blue" icon="🚚" />
        <StatCard label="今日起运" value={data.today_departed} tone="green" icon="🚀" />
        <StatCard label="今日到达" value={data.today_arrived} tone="green" icon="🏁" />
        <StatCard label="待签收" value={data.pending_sign} tone="amber" icon="📝" />
      </div>

      <div className="grid grid-2">
        <div className="card">
          <div className="card-title">
            <span>🚚 在途车辆</span>
            <Link to="/enterprise/orders" style={{ fontSize: 13 }}>运输单管理 →</Link>
          </div>
          {data.vehicle_list.length === 0 ? (
            <Empty text="暂无在途车辆" />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>车牌</th>
                  <th>路线</th>
                  <th>位置</th>
                  <th>最近上报</th>
                </tr>
              </thead>
              <tbody>
                {data.vehicle_list.map((v) => (
                  <tr key={v.transit_id}>
                    <td>
                      <Link to={`/enterprise/orders/${v.transit_id}`} style={{ fontWeight: 700 }}>
                        {v.plate_no}
                      </Link>
                    </td>
                    <td style={{ fontSize: 12 }}>{v.transit_no}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {v.lng.toFixed(4)}, {v.lat.toFixed(4)}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--color-ink-secondary)' }}>{fmtTime(v.last_gps_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card">
          <div className="card-title">⚠️ 未处理告警</div>
          <div style={{ fontSize: 14, lineHeight: 2 }}>
            <div>
              待处理告警：<strong style={{ color: 'var(--color-red)' }}>{data.alert_count}</strong> 条
            </div>
            <Link to="/gov/alerts" style={{ fontSize: 13 }}>
              前往告警台账处理 →
            </Link>
          </div>
          <div className="card-title" style={{ marginTop: 20 }}>
            最近运输单
          </div>
          {data.recent_orders.length === 0 ? (
            <Empty />
          ) : (
            data.recent_orders.slice(0, 5).map((o) => (
              <div key={o.id} className="row-between" style={{ padding: '8px 0', borderBottom: '1px solid var(--color-line)' }}>
                <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{o.transit_no}</span>
                <Link to={`/enterprise/orders/${o.id}`} style={{ fontSize: 13 }}>
                  详情 →
                </Link>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
